import { useMemo, useState } from 'react';

import {
  Button,
  Card,
  Icon,
  LoadingBlock,
  PageHeader,
  Select,
  StatusView,
  TextField,
  type SortDirection,
} from '@/components/ui';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { SessionsTable, sortSessions, type SessionSortKey } from '../components/SessionsTable';
import { useSessions } from '../hooks';
import { sessionIdentifier, titleFor, type SessionSummary } from '../model';

import styles from './SessionsPage.module.css';

type RawFilter = 'all' | 'with-raw' | 'without-raw';

const rawFilterOptions = [
  { value: 'all' as const, label: 'All sessions' },
  { value: 'with-raw' as const, label: 'With raw EEG' },
  { value: 'without-raw' as const, label: 'Without raw EEG' },
];

export function SessionsPage() {
  const { sessions, skipped, isPending, isError, error, refetch, isFetching } = useSessions();
  const compact = useMediaQuery('(max-width: 720px)');
  const locale = navigator.language.split('-')[0] ?? 'en';

  const [query, setQuery] = useState('');
  const [rawFilter, setRawFilter] = useState<RawFilter>('all');
  const [mode, setMode] = useState<string>('all');
  const [sort, setSort] = useState<{ key: SessionSortKey; direction: SortDirection }>({
    key: 'date',
    direction: 'desc',
  });

  const modeOptions = useMemo(() => {
    const modes = new Set<string>();
    for (const session of sessions) if (session.mode !== null) modes.add(session.mode);
    return [
      { value: 'all', label: 'All modes' },
      ...[...modes].sort().map((value) => ({ value, label: value })),
    ];
  }, [sessions]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();

    const filtered = sessions.filter((session) => {
      if (rawFilter === 'with-raw' && session.rawFileId === null) return false;
      if (rawFilter === 'without-raw' && session.rawFileId !== null) return false;
      if (mode !== 'all' && session.mode !== mode) return false;
      if (needle === '') return true;
      return matches(session, needle, locale);
    });

    return sortSessions(filtered, sort.key, sort.direction, locale);
  }, [sessions, query, rawFilter, mode, sort, locale]);

  const onSortChange = (key: SessionSortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'title' ? 'asc' : 'desc' },
    );

  const filtersActive = query.trim() !== '' || rawFilter !== 'all' || mode !== 'all';

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Sessions"
        description="Every meditation session recorded with your ela headband. Select one to inspect its metrics and raw EEG recording."
        actions={
          <Button icon="refresh" busy={isFetching && !isPending} onClick={() => void refetch()}>
            Refresh
          </Button>
        }
      />

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.search}>
            <TextField
              compact
              leadingIcon="search"
              placeholder="Search title, mode or identifier…"
              aria-label="Search sessions"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <Select
            label="Filter by raw EEG availability"
            value={rawFilter}
            options={rawFilterOptions}
            onValueChange={setRawFilter}
          />

          {modeOptions.length > 1 ? (
            <Select label="Filter by mode" value={mode} options={modeOptions} onValueChange={setMode} />
          ) : null}

          <span className={styles.spacer} />

          <span className={styles.count}>
            {visible.length === sessions.length
              ? `${sessions.length} session${sessions.length === 1 ? '' : 's'}`
              : `${visible.length} of ${sessions.length}`}
          </span>
        </div>

        {isPending ? (
          <LoadingBlock label="Loading your sessions…" />
        ) : isError ? (
          <StatusView
            icon="cloud-off"
            tone="danger"
            title="Could not load sessions"
            message={error instanceof Error ? error.message : 'Please try again.'}
            action={
              <Button variant="primary" icon="refresh" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          />
        ) : sessions.length === 0 ? (
          <StatusView
            icon="waves"
            title="No sessions yet"
            message="Meditation sessions recorded with your headband will appear here."
            action={
              <Button icon="refresh" onClick={() => void refetch()}>
                Refresh
              </Button>
            }
          />
        ) : visible.length === 0 ? (
          <StatusView
            icon="filter"
            compact
            title="No sessions match those filters"
            message="Try a different search term, or clear the filters to see everything."
            action={
              filtersActive ? (
                <Button
                  onClick={() => {
                    setQuery('');
                    setRawFilter('all');
                    setMode('all');
                  }}
                >
                  Clear filters
                </Button>
              ) : null
            }
          />
        ) : (
          <SessionsTable
            sessions={visible}
            sort={sort}
            onSortChange={onSortChange}
            locale={locale}
            compact={compact}
          />
        )}

        {skipped > 0 ? (
          <div className={styles.skipped}>
            <Icon name="alert" size={15} className={styles.skippedIcon} />
            <span>
              {skipped} session{skipped === 1 ? '' : 's'} could not be read and{' '}
              {skipped === 1 ? 'was' : 'were'} left out.
            </span>
          </div>
        ) : null}
      </Card>
    </>
  );
}

function matches(session: SessionSummary, needle: string, locale: string): boolean {
  if (titleFor(session, locale).toLowerCase().includes(needle)) return true;
  if (sessionIdentifier(session).toLowerCase().includes(needle)) return true;
  if (session.id.toLowerCase().includes(needle)) return true;
  if (session.mode !== null && session.mode.toLowerCase().includes(needle)) return true;
  if (session.contentId !== null && session.contentId.toLowerCase().includes(needle)) return true;
  return Object.values(session.titles).some((title) => title.toLowerCase().includes(needle));
}
